import { useContent } from 'contexts/Content/Content';
import { getUrl } from 'helpers';
import Head from 'next/head';

interface DocHeadProps {
    isStaging?: boolean;
}

export default function DocHead({ isStaging = false }: DocHeadProps) {
    const content = useContent();

    const {
        canonicalUrl,
        documentDescription,
        documentTitle,
        favicon,
        faq,
        siteName,
        socialDescription,
        socialImage,
        socialImageAlt,
        socialTitle,
    } = content.general;
    const shareDescription = socialDescription || documentDescription;
    const shareTitle = socialTitle || documentTitle;
    const webApplicationStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Experiment Calculator',
        applicationCategory: 'BusinessApplication',
        description: documentDescription,
        isAccessibleForFree: true,
        offers: {
            '@type': 'Offer',
            price: 0,
            priceCurrency: 'USD',
        },
        operatingSystem: 'Any',
        featureList: [
            'Recommended sample size estimation',
            'Confidence level and margin of error inputs',
            'Multi-variant binary result analysis',
            'Threshold-based winner evaluation',
            'Experiment export and sharing',
        ],
        url: canonicalUrl,
    };
    const faqStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(entry => ({
            '@type': 'Question',
            name: entry.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: entry.answer,
            },
        })),
    };

    return (
        <Head>
            {isStaging && <meta name="robots" content="noindex, nofollow" />}
            <title>{documentTitle}</title>
            <meta name="description" content={documentDescription} />
            <link rel="canonical" href={canonicalUrl} />
            <link rel="shortcut icon" href={getUrl(favicon)} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:title" content={shareTitle} />
            <meta property="og:description" content={shareDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={socialImage} />
            <meta property="og:image:alt" content={socialImageAlt} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={shareTitle} />
            <meta name="twitter:description" content={shareDescription} />
            <meta name="twitter:image" content={socialImage} />
            <meta name="twitter:image:alt" content={socialImageAlt} />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(webApplicationStructuredData) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
            />
        </Head>
    );
}
